import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { fetchLoginMenus, type ApiMenuItem } from '@/api/menu'
import {
  resolveMenuChildPath,
  resolveMenuIconComponent,
  childIconMap,
  iconMap,
  type MenuItem,
} from '@/layouts/config'

/** 前后端 path 别名：守卫按最终落地地址校验时仍视为同一菜单 */
const PATH_ACCESS_ALIASES: Record<string, readonly string[]> = {
  '/overseas/helpReport': ['/emergency/help-report/list'],
  '/emergency/help-report/list': ['/overseas/helpReport'],
  '/service/user-message/list': ['/other/user-message/list'],
  '/other/user-message/list': ['/service/user-message/list'],
}

/** 兼容接口 title/name */
function getNodeTitle(node: ApiMenuItem): string {
  return (node.title ?? node.name ?? '').trim()
}

/** 接口 path 标准化 */
function normalizePath(path?: string): string {
  if (!path) return ''
  const v = path.trim()
  if (!v) return ''
  return v.startsWith('/') ? v : `/${v}`
}

/** 与 Snowy SYS_RESOURCE.visible 一致：FALSE / N / 0 等为不可见；空则视为显示 */
function isMenuHidden(node: Pick<ApiMenuItem, 'visible'>): boolean {
  const v = (node.visible ?? '').trim().toUpperCase()
  return v === 'FALSE' || v === 'N' || v === '0' || v === 'NO' || v === 'HIDDEN'
}

/** 递归收集目录下可访问菜单（只保留有 path 的菜单节点） */
function collectLeafMenus(nodes: ApiMenuItem[] | undefined, out: ApiMenuItem[]) {
  if (!nodes?.length) return
  for (const n of nodes) {
    if (isMenuHidden(n)) continue
    const type = (n.menuType ?? '').toUpperCase()
    if (type === 'BUTTON') continue
    if (type === 'CATALOG') {
      collectLeafMenus(n.children, out)
      continue
    }
    const path = normalizePath(n.path)
    if (path) {
      out.push({ ...n, path })
    }
    if (n.children?.length) {
      collectLeafMenus(n.children, out)
    }
  }
}

/** 将 loginMenu 的“平台管理”模块转换为前端侧边栏结构 */
function transformPlatformMenus(apiMenus: ApiMenuItem[]): MenuItem[] {
  const platformModule = apiMenus.find((m) => getNodeTitle(m) === '平台管理')
  if (!platformModule?.children?.length) return []
  const defaultIcon = iconMap.FolderOutlined
  const catalogs = [...platformModule.children]
    .filter((c) => (c.menuType ?? '').toUpperCase() !== 'BUTTON' && !isMenuHidden(c))
    .sort((a, b) => (a.sortCode ?? 99999) - (b.sortCode ?? 99999))
  const result: MenuItem[] = []
  for (const catalog of catalogs) {
    const label = getNodeTitle(catalog)
    if (!label) continue
    const icon = (resolveMenuIconComponent(catalog.icon) ?? defaultIcon) as MenuItem['icon']
    const leafMenus: ApiMenuItem[] = []
    if ((catalog.menuType ?? '').toUpperCase() === 'CATALOG') {
      collectLeafMenus(catalog.children, leafMenus)
    } else {
      collectLeafMenus([catalog], leafMenus)
    }
    const children = leafMenus
      .sort((a, b) => (a.sortCode ?? 99999) - (b.sortCode ?? 99999))
      .map((m) => {
        const path = resolveMenuChildPath(
          m.id,
          normalizePath(m.path),
          (m.name ?? '').trim(),
          getNodeTitle(m),
        )
        return {
          key: m.id,
          label: getNodeTitle(m),
          path,
          icon:
            resolveMenuIconComponent(m.icon) ??
            childIconMap[m.id] ??
            childIconMap[(m.name ?? '').trim()] ??
            defaultIcon,
        }
      })
      .filter((x) => x.label && x.path)
    if (!children.length) continue
    const item: MenuItem = {
      key: catalog.id,
      label,
      icon,
      path: '',
      children,
    }
    result.push(item)
  }
  return result
}

export const useMenuStore = defineStore('menu', () => {
  const menus = ref<MenuItem[]>([])
  const loading = ref(false)
  const error = ref<Error | null>(null)
  const loaded = ref(false)
  let loadPromise: Promise<void> | null = null

  async function loadMenus() {
    if (loaded.value) return
    if (loadPromise) return loadPromise
    loading.value = true
    error.value = null
    loadPromise = (async () => {
      try {
        const data = await fetchLoginMenus()
        if (data?.length) {
          const transformed = transformPlatformMenus(data)
          if (transformed.length) {
            menus.value = transformed
            loaded.value = true
            return
          }
        }
        menus.value = []
        loaded.value = true
      } catch (e) {
        error.value = e instanceof Error ? e : new Error('菜单加载失败')
        menus.value = []
        loaded.value = false
      } finally {
        loading.value = false
        loadPromise = null
      }
    })()
    return loadPromise
  }

  /** 强制重新拉取登录菜单（菜单增删改 / 角色授权后调用） */
  async function reloadMenus() {
    reset()
    await loadMenus()
  }

  function reset() {
    menus.value = []
    loading.value = false
    error.value = null
    loaded.value = false
    loadPromise = null
  }

  /** 菜单变化时自动派生，避免每次路由守卫重建路径列表 */
  const accessiblePaths = computed(() => {
    const paths: string[] = []
    for (const menu of menus.value) {
      if (menu.path) paths.push(menu.path)
      for (const child of menu.children ?? []) {
        if (child.path) paths.push(child.path)
      }
    }
    return [...new Set(paths)]
  })

  function pathEqualsOrAliased(menuPath: string, currentPath: string): boolean {
    if (menuPath === currentPath) return true
    const aliases = PATH_ACCESS_ALIASES[menuPath]
    return Boolean(aliases?.includes(currentPath))
  }

  function hasAccessPath(path: string): boolean {
    const currentPath = path.split('?')[0] ?? path
    for (const p of accessiblePaths.value) {
      if (pathEqualsOrAliased(p, currentPath)) return true
      if (p.endsWith('/list')) {
        const base = p.slice(0, -'/list'.length)
        if (base && currentPath.startsWith(`${base}/`)) return true
      }
    }
    return false
  }

  /** 模块根（如 /warning）下第一个有权限的叶子；无则 null */
  function getFirstAccessiblePathInModule(modulePath: string): string | null {
    const prefix = modulePath.endsWith('/') ? modulePath.slice(0, -1) : modulePath
    if (!prefix) return getFirstAccessiblePath()
    for (const menu of menus.value) {
      for (const child of menu.children ?? []) {
        const p = child.path
        if (!p) continue
        if (p === prefix || p.startsWith(`${prefix}/`)) return p
        if (prefix === '/emergency' && p === '/overseas/helpReport') return p
      }
    }
    return null
  }

  function getFirstAccessiblePath(): string | null {
    for (const menu of menus.value) {
      for (const child of menu.children ?? []) {
        if (child.path) return child.path
      }
      if (menu.path) return menu.path
    }
    return null
  }

  return {
    menus,
    loading,
    error,
    loaded,
    loadMenus,
    reloadMenus,
    reset,
    hasAccessPath,
    getFirstAccessiblePath,
    getFirstAccessiblePathInModule,
  }
})
